import { useState, useEffect } from 'react';
import TWMLogo from '@/assets/logo_twm_only.png';
import {
    Box,
    Drawer,
    List,
    ListItemIcon,
    ListItemText,
    Typography,
    IconButton,
    ListItemButton,
} from '@mui/material';
import {
    BusinessCenterOutlined,
} from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import DesktopWindowsOutlined from '@mui/icons-material/DesktopWindowsOutlined';
import WorkOutline from '@mui/icons-material/WorkOutline';
import { useTranslation } from 'react-i18next'
import DashboardRedirect from '@/components/DashboardRedirect'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import startsWith from 'lodash/startsWith'
import { Outlet, useNavigate } from 'react-router-dom'
import useHistoryStack from '@/hooks/useHistoryStack'
import { connect } from 'react-redux'
import last from 'lodash/last'


const mapStateToProps = ({ app }) => ({
    scrollTo: app.scrollTo,
    scrollBy: app.scrollBy,
})

// Menu 寬度
const drawerWidth = 260;

// 個人資訊樣式
const navItemSx = (highlighted = false) => ({
    '&.MuiMenuItem-root': {
        fontWeight: highlighted ? 'bold' : 'normal',
        color: theme => {
            if (highlighted) {
                return theme.palette.primary.main
            }

            return theme.palette.other.black.main
        },

        '&:hover': {
            color: 'common.white',
            background: theme => theme.palette.primary.main,
        },
    },
})


const Dashboard = ({ dispatch }) => {

    const { t } = useTranslation()

    const navigate = useNavigate()

    const historyStack = useHistoryStack()

    const currentPath = last(historyStack.map(({ pathname: p }) => p))

    const [activeMenu, setActiveMenu] = useState(() => localStorage.getItem('activeMenu') || 'account');

    const profileSelected = startsWith(currentPath, '/personal_details')

    const [profileAnchor, setProfileAnchor] = useState()

    const PRIMARY_TEXT_COLOR = '#1F1F1F';

    const menuTitles = {
        account: '帳號管理',
        company: '公司管理',
        plan: '方案管理',
        activity: '活動清單',
        auditlog: '操作日誌',
        system: '系統設定',
        personal_details: '個人資料'
    };

    useEffect(() => {
        localStorage.setItem('activeMenu', activeMenu);
    }, [activeMenu]);

    return (
        <>
            <DashboardRedirect />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    gap: 2,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ display: 'flex', height: '100vh', fontFamily: 'Noto Sans TC' }}>
                    {/* 左側 Drawer */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            height: '100%',
                            width: drawerWidth,
                            flexShrink: 0,
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                backgroundColor: 'transparent',
                                borderRight: 'none',
                                display: 'flex',
                                justifyContent: 'center',
                            },
                        }}
                        PaperProps={{
                            sx: {
                                pl: 3,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 220,
                                height: '88%',
                                backgroundColor: '#f5f6fa',
                                borderRadius: 6,
                                px: 2,
                                py: 3,
                                mb: 6
                            }}
                        >
                            {/* Logo */}
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <img src={TWMLogo} alt="TWM Logo" style={{ width: 60 }} />
                                <Typography sx={{ fontWeight: 700, fontSize: 24, mt: 2 }}>
                                    後臺管理
                                </Typography>
                            </Box>

                            {/* Menu */}
                            <List sx={{ px: 1 }}>
                                {/* 帳號管理 */}
                                <ListItemButton
                                    onClick={() => {
                                        setActiveMenu('account');
                                        navigate('/account');
                                    }}
                                    selected={activeMenu === 'account'}
                                    sx={{
                                        borderRadius: 3,
                                        mb: 0.5,
                                    }}
                                >
                                    <ListItemIcon sx={{ color: PRIMARY_TEXT_COLOR, minWidth: 36 }}>
                                        <GroupsOutlined />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="帳號管理"
                                        primaryTypographyProps={{ fontWeight: 600, fontSize: 16 }}
                                    />
                                </ListItemButton>

                                {/* 子選單 */}
                                <List disablePadding>
                                    <ListItemButton
                                        onClick={() => {
                                            setActiveMenu('company');
                                            navigate('/company');
                                        }}
                                        selected={activeMenu === 'company'}
                                        sx={{ borderRadius: 3 }}
                                    >
                                        <ListItemIcon sx={{ color: PRIMARY_TEXT_COLOR, minWidth: 36 }}>
                                            <BusinessCenterOutlined />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="公司管理"
                                            primaryTypographyProps={{
                                                fontWeight: 700,
                                                fontSize: 16,
                                                color: PRIMARY_TEXT_COLOR,
                                            }}
                                        />
                                    </ListItemButton>

                                    <ListItemButton
                                        onClick={() => {
                                            setActiveMenu('plan');
                                            navigate('/plan');
                                        }}
                                        selected={activeMenu === 'plan'}
                                        sx={{ borderRadius: 3 }}
                                    >
                                        <ListItemIcon sx={{ color: PRIMARY_TEXT_COLOR, minWidth: 36 }}>
                                            <WorkOutline />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="方案管理"
                                            primaryTypographyProps={{
                                                fontWeight: 700,
                                                fontSize: 16,
                                                color: PRIMARY_TEXT_COLOR,
                                            }}
                                        />
                                    </ListItemButton>
                                </List>

                                {/* 其他 */}
                                <ListItemButton
                                    onClick={() => {
                                        setActiveMenu('activity'); // 切換右側畫面
                                        navigate('/activity');
                                    }}
                                    selected={activeMenu === 'activity'}
                                >
                                    <ListItemIcon sx={{ color: PRIMARY_TEXT_COLOR, minWidth: 36 }}>
                                        <AssignmentOutlined />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="活動清單"
                                        primaryTypographyProps={{
                                            fontWeight: 700,
                                            fontSize: 16,
                                            color: PRIMARY_TEXT_COLOR,
                                        }}
                                    />
                                </ListItemButton>

                                <ListItemButton
                                onClick={() => {
                                    setActiveMenu('auditlog'); // 切換右側畫面
                                    navigate('/auditlog');
                                }}
                                selected={activeMenu === 'auditlog'}
                                >
                                    <ListItemIcon sx={{ color: PRIMARY_TEXT_COLOR, minWidth: 36 }}>
                                        <MenuBookOutlined />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="操作日誌"
                                        primaryTypographyProps={{
                                            fontWeight: 700,
                                            fontSize: 16,
                                            color: PRIMARY_TEXT_COLOR,
                                        }}
                                    />
                                </ListItemButton>

                                <ListItemButton>
                                    <ListItemIcon sx={{ color: PRIMARY_TEXT_COLOR, minWidth: 36 }}>
                                        <DesktopWindowsOutlined />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="系統設定"
                                        primaryTypographyProps={{
                                            fontWeight: 700,
                                            fontSize: 16,
                                            color: PRIMARY_TEXT_COLOR,
                                        }}
                                    />
                                </ListItemButton>
                            </List>
                        </Box>
                    </Drawer>

                    {/* 右側主區 */}
                    <Box sx={{ flexGrow: 1, pt: 4, pl: 1, pr: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                backgroundColor: '#ececf1',
                                borderRadius: 3,
                                px: 3,
                                py: 2,
                                mb: 3,
                            }}
                        >
                            <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
                                {menuTitles[activeMenu] || ''}
                            </Typography>

                            <IconButton
                                color={profileSelected || profileAnchor ? 'primary' : 'secondary_80'}
                                onClick={e => {
                                    setProfileAnchor(e.currentTarget)
                                }}
                            >
                                <PersonIcon />
                            </IconButton>

                            <Menu
                                anchorEl={profileAnchor}
                                open={!!profileAnchor}
                                onClose={() => {
                                    setProfileAnchor(null)
                                }}
                                MenuListProps={{
                                    sx: { py: 0 },
                                }}
                            >
                                <MenuItem
                                    sx={navItemSx(profileSelected)}
                                    onClick={() => {
                                        navigate('/personal_details', { replace: true })
                                        setProfileAnchor(null)
                                        setActiveMenu('personal_details');
                                    }}
                                >
                                    {t('general.personal_details')}
                                </MenuItem>

                                <MenuItem
                                    sx={navItemSx()}
                                    onClick={() => {
                                        localStorage.clear() 
                                        dispatch({ type: 'auth/logout' })
                                    }}
                                >
                                    {t('general.logout')}
                                </MenuItem>
                            </Menu>

                        </Box>

                        <Outlet />

                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default connect(mapStateToProps)(Dashboard);